import { Component } from "@theme/component";
import { sectionRenderer } from "@theme/section-renderer";
import { requestIdleCallback, viewTransition } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";
import { PaginatedListAspectRatioHelper } from "@theme/paginated-list-aspect-ratio";

/**
 * Status text for a Load more batch, e.g. "24 more products loaded, 52 of 109 shown".
 * @param {number} added
 * @param {number} shown
 * @param {number} total
 * @param {string} one - Singular template. Placeholders: {{ shown }}, {{ total }}.
 * @param {string} other - Plural template. Placeholders: {{ count }}, {{ shown }}, {{ total }}.
 * @returns {string}
 */
export function formatLoadMoreStatus(added, shown, total, one, other) {
  const template = added === 1 ? one : other;
  return template
    .replaceAll("{{ count }}", String(added))
    .replaceAll("{{ shown }}", String(shown))
    .replaceAll("{{ total }}", String(total));
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLAnchorElement | HTMLButtonElement} [loadMoreButton] - The Load more control (when infinite scroll is off).
 * @property {HTMLElement} [loadMoreStatus] - Polite live region for the Load more result.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    if (!this.isLocalReveal) {
      this.#fetchPage("next");
      this.#fetchPage("previous");
    }
    this.#observeViewMore();
    this.#bindLoadMoreButton();

    // Listen for filter updates to clear cached pages
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  /**
   * When true, every item is already rendered in the DOM (sorted correctly server-side)
   * and "Load more"/infinite scroll simply reveals already-present, hidden cards in
   * batches instead of fetching a new page from the server.
   *
   * Used by collection/search results so sorting (e.g. price) stays correct across the
   * whole list — Shopify's native per-page pagination can't guarantee that (see
   * main-collection.liquid).
   *
   * @returns {boolean}
   */
  get isLocalReveal() {
    return this.hasAttribute("local-reveal");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#onLoadMoreClick);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Drop old sentinels after section morph / filter (replaced nodes stay observed otherwise)
    this.infinityScrollObserver?.disconnect();

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                // Every batch before the currently revealed range is already visible
                // in local-reveal mode — nothing to do.
                if (!this.isLocalReveal) this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.isLocalReveal
                  ? this.#revealNextBatch()
                  : this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: "100px",
        },
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage))
      return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === "next") {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("page", pageNumber.toString());
      newUrl.hash = "";
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      pageInfo.url,
    );
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Renders and appends the next page of products (infinite-style, same as scroll).
   * @param {{ updateUrl?: boolean }} [options] - When updateUrl is false (Load more button), URL is not changed so the page does not redirect.
   * @returns {Promise<HTMLElement[]>} Cards appended for this page.
   */
  async #renderNextPage(options = {}) {
    const { updateUrl = true } = options;
    const { grid } = this.refs;

    if (!grid) return [];

    const nextPage = this.#getPage("next");

    if (!nextPage || !this.#shouldUsePage(nextPage)) return [];
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage("next");

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return [];
    }

    const appended = /** @type {HTMLElement[]} */ (
      Array.from(nextPageItemElements).filter(
        (node) => node instanceof HTMLElement,
      )
    );
    grid.append(...appended);

    this.#aspectRatioHelper.processNewElements();

    // Only update URL when triggered by infinite scroll; Load more button keeps current URL (no redirect).
    if (updateUrl) {
      history.pushState("", "", nextPage.url.toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage("next");
    });

    return appended;
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage("previous");
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage("previous");

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement
      ? firstElement.getBoundingClientRect().top + window.scrollY
      : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState("", "", previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight =
        firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: "instant",
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage("previous");
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === "previous";

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.hash = "";

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, "text/html");
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Not `:scope >` — theme blocks (e.g. a promo card) render one level deeper, wrapped
    // in Shopify's own `.shopify-block` div, so a direct-child selector would drop them.
    return gridElement.querySelectorAll(':scope [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute("section-id");

    if (!id) throw new Error("The section-id attribute is required");

    return id;
  }

  /**
   * Loads the next page and appends products (infinite loading, no redirect).
   * Used by the Load more button; same behavior as enable_infinite_scroll but triggered by click.
   * @returns {Promise<HTMLElement[]>} Cards revealed or appended by this batch.
   */
  async loadMore() {
    if (this.isLocalReveal) return this.#revealNextBatch();
    return this.#renderNextPage({ updateUrl: false });
  }

  /**
   * Reveals the next batch of already-rendered (hidden) cards. Used instead of
   * `#renderNextPage` when `isLocalReveal` is true.
   * @returns {HTMLElement[]}
   */
  #revealNextBatch() {
    const { grid } = this.refs;
    if (!grid) return [];

    const hiddenCards = grid.querySelectorAll(
      ':scope > [ref="cards[]"][hidden]',
    );
    if (!hiddenCards.length) return [];

    const nextBatch = Math.min(
      ...Array.from(hiddenCards, (card) =>
        Number(/** @type {HTMLElement} */ (card).dataset.page || Infinity),
      ),
    );

    /** @type {HTMLElement[]} */
    const revealed = [];
    for (const card of hiddenCards) {
      if (
        Number(/** @type {HTMLElement} */ (card).dataset.page) === nextBatch
      ) {
        card.removeAttribute("hidden");
        revealed.push(/** @type {HTMLElement} */ (card));
      }
    }

    this.#aspectRatioHelper?.processNewElements();
    return revealed;
  }

  /**
   * Load more: delegate clicks from the host so a new button after filter/section morph still works
   * (direct addEventListener on ref only runs once in connectedCallback).
   */
  #bindLoadMoreButton() {
    this.addEventListener("click", this.#onLoadMoreClick);
  }

  /** @param {MouseEvent} event */
  #onLoadMoreClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const loadMoreButton = event.target.closest(".load-more-button");
    if (!loadMoreButton || !this.contains(loadMoreButton)) return;

    event.preventDefault();

    const { grid } = this.refs;
    if (!grid) return;

    const wrapper = loadMoreButton.closest(".load-more-wrapper");
    const defaultText = loadMoreButton.dataset.defaultText || "Load more";
    const loadingText = loadMoreButton.dataset.loadingText || "Loading...";

    if (loadMoreButton.getAttribute("aria-disabled") === "true") return;

    // aria-disabled keeps the focused control focusable. The disabled property
    // would blur it and drop focus to document.body.
    loadMoreButton.setAttribute("aria-disabled", "true");
    loadMoreButton.textContent = loadingText;

    /** @type {HTMLElement[]} */
    let addedCards = [];
    try {
      addedCards = await this.loadMore();
    } catch (error) {
      if (loadMoreButton.isConnected) {
        loadMoreButton.removeAttribute("aria-disabled");
        loadMoreButton.textContent = defaultText;
      }
      throw error;
    }

    if (!this.isConnected) {
      if (loadMoreButton.isConnected) {
        loadMoreButton.removeAttribute("aria-disabled");
        loadMoreButton.textContent = defaultText;
      }
      return;
    }

    const productCards = addedCards.filter((card) => card.dataset.productId);
    const focusCard = productCards[0] ?? addedCards[0];
    if (focusCard) this.#focusNewCard(focusCard);
    if (productCards.length) {
      this.#announceLoadedProducts(grid, productCards.length);
    }

    if (loadMoreButton.isConnected) {
      loadMoreButton.removeAttribute("aria-disabled");
      loadMoreButton.textContent = defaultText;
    }

    const nextPage = this.#getPage("next");
    if (loadMoreButton instanceof HTMLAnchorElement && nextPage) {
      loadMoreButton.href = nextPage.url.toString();
    }

    const noMoreLeft = this.isLocalReveal
      ? !grid.querySelector(':scope > [ref="cards[]"][hidden]')
      : (() => {
          const cards = grid.querySelectorAll("[data-page]");
          const lastCard = cards[cards.length - 1];
          const lastPage = Number(grid.dataset.lastPage || 0);
          const lastCardPage = lastCard
            ? Number(/** @type {HTMLElement} */ (lastCard).dataset.page || 0)
            : 0;
          return Boolean(lastCard && lastPage && lastCardPage >= lastPage);
        })();

    if (wrapper && noMoreLeft) {
      const active = document.activeElement;
      if (
        active === loadMoreButton ||
        (active instanceof Node && wrapper.contains(active))
      ) {
        const fallback =
          focusCard ??
          grid.querySelector(":scope > [data-product-id]:not([hidden])");
        if (fallback instanceof HTMLElement) this.#focusNewCard(fallback);
      }
      /** @type {HTMLElement} */ (wrapper).style.display = "none";
    }
  };

  /**
   * Move keyboard focus to the start of the batch that just loaded.
   * tabindex -1 accepts programmatic focus without joining the tab order.
   * @param {HTMLElement} card
   */
  #focusNewCard(card) {
    card.tabIndex = -1;
    card.focus({ focusVisible: true });
  }

  /**
   * @param {HTMLElement} grid
   * @param {number} added
   */
  #announceLoadedProducts(grid, added) {
    const status = this.refs.loadMoreStatus;
    if (!status) return;

    const shown = grid.querySelectorAll(
      ":scope > [data-product-id]:not([hidden])",
    ).length;
    const inDom = grid.querySelectorAll(":scope > [data-product-id]").length;
    const declared = Number(grid.dataset.totalCount) || 0;
    const total = Math.max(declared, inDom);
    const one =
      Theme?.translations?.load_more_status_one ||
      "1 more product loaded, {{ shown }} of {{ total }} shown";
    const other =
      Theme?.translations?.load_more_status_other ||
      "{{ count }} more products loaded, {{ shown }} of {{ total }} shown";

    status.textContent = formatLoadMoreStatus(added, shown, total, one, other);
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      const gridEl = this.refs.grid;
      const newLastPage = gridEl?.dataset.lastPage;
      const gridChildrenReplaced =
        !!gridEl &&
        mutations.some((m) => m.type === "childList" && m.target === gridEl);

      // Page count can stay the same after filtering; grid children still swap — re-bind infinite scroll + prefetch
      if (newLastPage !== currentLastPage || gridChildrenReplaced) {
        observer.disconnect();

        if (!this.isConnected) {
          return;
        }

        this.#observeViewMore();
        // Local-reveal mode ships every batch already correctly hidden/revealed from
        // the fresh server render — nothing to prefetch.
        if (!this.isLocalReveal) this.#fetchPage("next");
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ["data-last-page"],
        childList: true,
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
